import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { ROUTES, getCaseRoute } from '@/config/routes';
import { useMasterclass } from '@/contexts/MasterclassContext';
import { useIsAccreditationUser } from '@/hooks/useIsAccreditationUser';
import { supabase } from '@/integrations/supabase/client';
import { useCasesConfig } from '@/modules/case/hooks/useCasesConfig';
import { fetchCaseIntroVideoUrl } from '@/modules/case/utils/asset-loader';
import { trackEvent } from '@/modules/google-analytics/analytic/analytics';
import { captureSentry } from '@/utils/sentryHelper';

import { CaseSelectorConfig } from '../types';

export const useCaseSelector = (config: CaseSelectorConfig) => {
  const navigate = useNavigate();
  const { getCaseMetadata, cases } = useCasesConfig();
  const {
    showIntroVideo,
    setShowIntroVideo,
    showConfirmationVideo,
    setShowConfirmationVideo,
    setSelectedCase,
    setHasSeenIntro,
    showLoadingAnimation,
    setShowLoadingAnimation,
    caseProgress,
    loading,
    resetCaseProgress,
    resetAccreditationUserSlidePosition,
  } = useMasterclass();
  const isAccreditationUser = useIsAccreditationUser();

  const [pendingCase, setPendingCase] = useState<string | null>(null);
  const [certificationCase, setCertificationCase] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [caseIntroVideoUrl, setCaseIntroVideoUrl] = useState<string | null>(null);

  // Check authentication and onboarding status
  useEffect(() => {
    const checkAuth = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        navigate(ROUTES.AUTH);
      } else {
        setUserId(user.id);

        // Track case selector visit for all cases
        (async () => {
          // Get all case IDs
          const caseIds = cases.map((c) => c.id);

          // Increment case_selector_visits for all cases
          for (const caseId of caseIds) {
            const { data: stats } = await supabase
              .from('case_statistics')
              .select('case_selector_visits')
              .eq('user_id', user.id)
              .eq('case_id', caseId)
              .maybeSingle();

            await supabase.from('case_statistics').upsert(
              {
                user_id: user.id,
                case_id: caseId,
                case_selector_visits: (stats?.case_selector_visits || 0) + 1,
              },
              {
                onConflict: 'user_id,case_id',
              }
            );
          }
        })().catch((error) => {
          console.error('Error tracking case selector visit:', error);
          captureSentry({
            title: 'Failed to track case selector visit',
            error,
            detail: { userId: user.id },
          });
        });

        // Check if user has seen onboarding
        const { data: profile } = await supabase
          .from('profiles')
          .select('has_seen_onboarding')
          .eq('id', user.id)
          .single();

        // Show onboarding by default unless user has explicitly seen it
        if (config.modals.showIntroOnFirstVisit) {
          // Show if: no profile yet OR profile exists but hasn't seen onboarding
          const shouldShow = !profile || !profile.has_seen_onboarding;
          setShowIntroVideo(shouldShow);
        } else {
          setShowIntroVideo(false);
        }
      }
    };

    void checkAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!session) {
        navigate(ROUTES.AUTH);
      } else {
        setUserId(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, config.modals.showIntroOnFirstVisit, setShowIntroVideo]);

  // Reset confirmation modal on component mount to prevent it from showing on page return
  useEffect(() => {
    setShowConfirmationVideo(false);
  }, [setShowConfirmationVideo]);

  const handleIntroClose = async () => {
    setShowIntroVideo(false);
    setHasSeenIntro(true);

    // Mark onboarding as seen in database (create profile if doesn't exist)
    if (userId) {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      await supabase.from('profiles').upsert(
        {
          id: userId,
          email: user?.email || '',
          has_seen_onboarding: true,
        },
        {
          onConflict: 'id',
        }
      );
    }
  };

  const handleCaseSelect = async (caseId: string) => {
    trackEvent('Case Selection', 'Select Case', caseId, null);
    if (userId) {
      (async () => {
        const { data: stats } = await supabase
          .from('case_statistics')
          .select('case_clicks')
          .eq('user_id', userId)
          .eq('case_id', caseId)
          .maybeSingle();

        await supabase.from('case_statistics').upsert(
          {
            user_id: userId,
            case_id: caseId,
            case_clicks: (stats?.case_clicks || 0) + 1,
          },
          {
            onConflict: 'user_id,case_id',
          }
        );
      })().catch((error) => {
        console.error('Error tracking case click:', error);
        captureSentry({
          title: 'Failed to track case click',
          error,
          detail: { userId, caseId },
        });
      });
    }

    // Get case metadata to get the caseKey
    const metadata = getCaseMetadata(caseId);
    if (!metadata) return;

    const caseKey = metadata.caseKey;

    // Check if case is already started or completed
    const progress = caseProgress[caseId];
    const isStartedOrCompleted =
      progress &&
      (progress.completed || (progress.visitedSlideIds && progress.visitedSlideIds.length > 0));

    // Handle reset based on user type
    if (caseProgress[caseId]?.completed) {
      if (isAccreditationUser) {
        // Accreditation users: only reset slide position, keep answers
        await resetAccreditationUserSlidePosition(caseId);
      } else {
        // Regular users: full reset (answers + progress)
        await resetCaseProgress(caseId);
      }
    }

    // Skip confirmation modal if case is already started or completed
    if (isStartedOrCompleted || !config.modals.showConfirmationBeforeStart) {
      startCase(caseKey);
    } else {
      // Fetch case intro video before showing confirmation modal
      const videoUrl = await fetchCaseIntroVideoUrl(metadata);
      setCaseIntroVideoUrl(videoUrl);

      setPendingCase(caseKey);
      setShowConfirmationVideo(true);
    }
  };

  const handleConfirmationClose = () => {
    setShowConfirmationVideo(false);
    setPendingCase(null);
  };

  const handleConfirmationStart = async () => {
    setShowConfirmationVideo(false);

    // Mark onboarding as seen when user starts their first case (create profile if doesn't exist)
    if (userId && pendingCase) {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      await supabase.from('profiles').upsert(
        {
          id: userId,
          email: user?.email || '',
          has_seen_onboarding: true,
        },
        {
          onConflict: 'id',
        }
      );

      startCase(pendingCase);
    }
  };

  const startCase = (caseKey: string) => {
    setShowLoadingAnimation(true);

    setTimeout(() => {
      setSelectedCase(caseKey);
      setShowLoadingAnimation(false);
      navigate(getCaseRoute(caseKey));
    }, config.navigation.loadingDuration);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate(ROUTES.AUTH);
  };

  return {
    userId,
    pendingCase,
    certificationCase,
    setCertificationCase,
    showIntroVideo,
    showConfirmationVideo,
    isLoading: showLoadingAnimation,
    caseProgress,
    caseIntroVideoUrl,
    handleIntroClose,
    handleCaseSelect,
    handleConfirmationClose,
    handleConfirmationStart,
    handleSignOut,
    caseProgressLoading: loading,
  };
};
